import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { api, hasToken } from './api';

/**
 * Preferências de notificação por usuário — matriz tipo × canal.
 *
 * **Backend-first (2026-05-17):** dados vivem em `notification_preferences`
 * (SQLite) via `/api/v1/notification-preferences/:userId/:eventType`. Sem
 * `localStorage` pra dados de negócio (regra inviolável).
 *
 * API pública mantida (`isChannelEnabled`, `getEnabledChannels`, `setPreference`,
 * `useNotificationPreferences`, `useNotificationPreferenceActions`) — sync
 * reads usam cache em memória hidratado pelo `useNotificationPreferences`.
 *
 * Regra 8.1 do Padrão Kanban IGM: in-app é SEMPRE on (não-desligável). Backend
 * silently no-op se tentar `PUT in-app enabled=false`; frontend filtra antes.
 */

export type NotificationEventType =
  | 'mention_in_comment'
  | 'deal_assigned'
  | 'task_deadline_approaching'
  | 'comment_in_followed_deal'
  | 'automation_triggered'
  // UI-2 + UI-3 + UI-5 (consome backend de NOTIF-1 / PR #24):
  | 'card_collaborator_added'
  | 'card_collaborator_removed'
  | 'task_approval_request'
  | 'task_approval_decided'
  | 'task_invitation_received'
  | 'mention_internal_note'
  | 'celebracao';

export type NotificationChannel = 'in-app' | 'email' | 'whatsapp' | 'push';

export const EVENT_TYPES: ReadonlyArray<{
  slug: NotificationEventType;
  label: string;
  description: string;
}> = [
  {
    slug: 'mention_in_comment',
    label: 'Menção em comentário',
    description: 'Alguém te mencionou (@você) num comentário de um card.',
  },
  {
    slug: 'deal_assigned',
    label: 'Card atribuído a você',
    description: 'Um card foi atribuído com você como responsável.',
  },
  {
    slug: 'task_deadline_approaching',
    label: 'Prazo se aproximando',
    description: 'Uma tarefa atribuída a você tem prazo nas próximas 24h.',
  },
  {
    slug: 'comment_in_followed_deal',
    label: 'Comentário em card seguido',
    description: 'Um card que você segue recebeu novo comentário.',
  },
  {
    slug: 'automation_triggered',
    label: 'Automação executada',
    description: 'Uma automação configurada por admin foi disparada em um card que envolve você.',
  },
  // UI-2 + UI-3 + UI-5 (PR #24 backend)
  {
    slug: 'card_collaborator_added',
    label: 'Adicionado como colaborador',
    description: 'Você foi adicionado como colaborador de um card.',
  },
  {
    slug: 'card_collaborator_removed',
    label: 'Removido de colaboração',
    description: 'Você foi removido dos colaboradores de um card.',
  },
  {
    slug: 'task_approval_request',
    label: 'Aprovação de tarefa pendente',
    description: 'Um colaborador pediu sua aprovação pra agendar uma tarefa.',
  },
  {
    slug: 'task_approval_decided',
    label: 'Sua tarefa foi decidida',
    description: 'Aprovação da sua tarefa foi decidida (aprovada ou rejeitada).',
  },
  {
    slug: 'task_invitation_received',
    label: 'Convite pra tarefa',
    description: 'Você foi convidado pra participar de uma tarefa.',
  },
  {
    slug: 'mention_internal_note',
    label: 'Menção em nota interna',
    description: 'Alguém te mencionou (@você) numa nota interna privada.',
  },
  {
    slug: 'celebracao',
    label: 'Celebração',
    description: 'Notificação de celebração quando um marco é alcançado.',
  },
] as const;

export const CHANNELS: ReadonlyArray<{
  slug: NotificationChannel;
  label: string;
  alwaysOn: boolean;
}> = [
  { slug: 'in-app', label: 'No app', alwaysOn: true },
  { slug: 'email', label: 'Email', alwaysOn: false },
  { slug: 'whatsapp', label: 'WhatsApp', alwaysOn: false },
  { slug: 'push', label: 'Push', alwaysOn: false },
] as const;

export type PreferencesMatrix = Partial<Record<NotificationEventType, NotificationChannel[]>>;

const DEFAULTS: PreferencesMatrix = {
  mention_in_comment: ['in-app', 'email'],
  deal_assigned: ['in-app'],
  task_deadline_approaching: ['in-app'],
  comment_in_followed_deal: ['in-app'],
  automation_triggered: ['in-app'],
  // UI-2 + UI-3 + UI-5
  card_collaborator_added: ['in-app'],
  card_collaborator_removed: ['in-app'],
  task_approval_request: ['in-app', 'email'],
  task_approval_decided: ['in-app'],
  task_invitation_received: ['in-app', 'email'],
  mention_internal_note: ['in-app'],
  celebracao: ['in-app'],
};

// ─── Cache em memória hidratado pelo hook ──────────────────────────────

const cache = new Map<string, PreferencesMatrix>();

function readCache(userId: string): PreferencesMatrix {
  return cache.get(userId) ?? DEFAULTS;
}

function setCache(userId: string, matrix: PreferencesMatrix): void {
  cache.set(userId, matrix);
}

// ─── API pública SYNC ──────────────────────────────────────────────────

/**
 * Verifica se um canal está ativo pra um tipo de evento de um usuário.
 * In-app é SEMPRE true — garantia do padrão (regra 8.1).
 */
export function isChannelEnabled(
  userId: string,
  eventType: NotificationEventType,
  channel: NotificationChannel
): boolean {
  if (channel === 'in-app') return true;
  const prefs = readCache(userId);
  const enabled = prefs[eventType] ?? DEFAULTS[eventType] ?? ['in-app'];
  return enabled.includes(channel);
}

/** Retorna os canais ativos pra um evento (com in-app sempre presente). */
export function getEnabledChannels(
  userId: string,
  eventType: NotificationEventType
): NotificationChannel[] {
  const prefs = readCache(userId);
  const stored = prefs[eventType] ?? DEFAULTS[eventType] ?? ['in-app'];
  return stored.includes('in-app') ? stored : ['in-app', ...stored];
}

// ─── Mutations (assíncronas) ───────────────────────────────────────────

/**
 * Persiste mudança de preferência via backend. Atualiza cache otimisticamente
 * antes de aguardar resposta — UI re-renderiza no mesmo tick.
 * In-app não pode ser desligado (silently no-op).
 */
export async function setPreference(
  userId: string,
  eventType: NotificationEventType,
  channel: NotificationChannel,
  enabled: boolean
): Promise<void> {
  if (channel === 'in-app' && !enabled) return;

  // Otimismo: atualiza cache imediatamente
  const current = readCache(userId);
  const currentChannels = current[eventType] ?? DEFAULTS[eventType] ?? ['in-app'];
  const nextChannels: NotificationChannel[] = enabled
    ? currentChannels.includes(channel)
      ? currentChannels
      : [...currentChannels, channel]
    : currentChannels.filter((c) => c !== channel);
  setCache(userId, { ...current, [eventType]: nextChannels });

  try {
    await api.put(`/api/v1/notification-preferences/${userId}/${eventType}`, {
      channel,
      enabled,
    });
  } catch {
    // Rollback otimista — restaura cache anterior
    setCache(userId, current);
    throw new Error('Falha ao salvar preferência');
  }
}

// ─── Hook reativo ──────────────────────────────────────────────────────

export function useNotificationPreferences(userId: string): PreferencesMatrix {
  const { data } = useQuery({
    queryKey: ['v1', 'notification-preferences', userId],
    queryFn: async () => {
      const raw = await api.get<Record<string, string[]>>(
        `/api/v1/notification-preferences/${userId}`
      );
      // Mescla com DEFAULTS pra eventos não cadastrados ainda no backend
      const matrix: PreferencesMatrix = { ...DEFAULTS };
      for (const [eventType, channels] of Object.entries(raw)) {
        if (Array.isArray(channels)) {
          // Filtra apenas canais conhecidos
          const valid = channels.filter((c): c is NotificationChannel =>
            ['in-app', 'email', 'whatsapp', 'push'].includes(c)
          );
          // Garante in-app sempre presente (regra 8.1)
          matrix[eventType as NotificationEventType] = valid.includes('in-app')
            ? valid
            : ['in-app', ...valid];
        }
      }
      setCache(userId, matrix);
      return matrix;
    },
    enabled: hasToken() && !!userId && userId !== 'anonymous',
    staleTime: 60_000,
  });
  return data ?? readCache(userId);
}

export function useNotificationPreferenceActions(userId: string) {
  const qc = useQueryClient();
  const set = useCallback(
    async (
      eventType: NotificationEventType,
      channel: NotificationChannel,
      enabled: boolean
    ) => {
      await setPreference(userId, eventType, channel, enabled);
      await qc.invalidateQueries({
        queryKey: ['v1', 'notification-preferences', userId],
      });
    },
    [qc, userId]
  );

  /**
   * UI-4 / TD-V2-NOTIF-03: copia prefs de outro user. Substitui prefs
   * existentes (não merge). Invalida cache local + backend cache em memória.
   */
  const copyFrom = useCallback(
    async (sourceUserId: string): Promise<{ copied: number }> => {
      const res = await api.post<{ ok: true; copied: number }>(
        `/api/v1/notification-preferences/${encodeURIComponent(userId)}/copy-from/${encodeURIComponent(sourceUserId)}`,
      );
      // Limpa cache em memória do user atual pra forçar reload
      cache.delete(userId);
      await qc.invalidateQueries({
        queryKey: ['v1', 'notification-preferences', userId],
      });
      return { copied: res.copied };
    },
    [qc, userId],
  );

  /**
   * UI-4 / TD-V2-NOTIF-02: bulk set — aplica `enabled` a TODOS os event_types
   * pra um canal específico. Sequencial pra preservar ordering otimista do cache.
   * Retorna a lista de eventTypes processados.
   */
  const setAllForChannel = useCallback(
    async (channel: NotificationChannel, enabled: boolean): Promise<NotificationEventType[]> => {
      if (channel === 'in-app' && !enabled) {
        // in-app não pode ser desligado em massa
        return [];
      }
      const types = EVENT_TYPES.map((e) => e.slug);
      for (const evt of types) {
        await setPreference(userId, evt, channel, enabled);
      }
      await qc.invalidateQueries({
        queryKey: ['v1', 'notification-preferences', userId],
      });
      return types;
    },
    [qc, userId],
  );

  return { setPreference: set, copyFrom, setAllForChannel };
}

// ─── Test helpers ──────────────────────────────────────────────────────

/** SÓ tests — limpa cache em memória. */
export function __resetAllPreferencesForTests(): void {
  cache.clear();
}

/** SÓ tests — popula cache pra fluxos sync sem precisar de React Query. */
export function __seedPreferencesCacheForTests(
  userId: string,
  matrix: PreferencesMatrix
): void {
  setCache(userId, matrix);
}
